import { Badge, Button, DropdownMenu } from '@components'
import { IWorkflow } from '@query'
import { formatDate, formatRelativeTime } from '@utils'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaEllipsisH, FaPause, FaPlay, FaTrash } from 'react-icons/fa'
import styled from 'styled-components'
import { ThumbnailWrapper, WorkflowThumbnail } from './WorkflowThumbnail'
import { WorkflowSortField } from './workflowSortUtils'

interface Props {
  workflow: IWorkflow
  sortField: WorkflowSortField
  onOpen: () => void
  onToggleEnabled: (enabled: boolean) => void
  onDelete: () => void
}

const getDateLabel = (
  workflow: IWorkflow,
  sortField: WorkflowSortField,
  t: (key: string, options?: Record<string, string>) => string
): string => {
  if (sortField === 'created_at') {
    return t('workflows.card.createdAt', { date: formatDate(workflow.created_at) })
  }

  if (sortField === 'updated_at') {
    return t('workflows.card.updatedRelative', { value: formatRelativeTime(workflow.updated_at) })
  }

  return t('workflows.card.updatedAt', { date: formatDate(workflow.updated_at) })
}

export const WorkflowCard: React.FC<Props> = ({
  workflow,
  sortField,
  onOpen,
  onToggleEnabled,
  onDelete
}) => {
  const [t] = useTranslation()
  const nodeCount = workflow.react_flow_data?.nodes?.length || 0

  const menuItems = [
    workflow.enabled ? (
      <Button
        key="pause"
        type="button"
        isSecondary
        icon={<FaPause />}
        onClick={() => onToggleEnabled(false)}
      >
        {t('workflows.card.menu.pause')}
      </Button>
    ) : (
      <Button
        key="activate"
        type="button"
        isSecondary
        icon={<FaPlay />}
        onClick={() => onToggleEnabled(true)}
      >
        {t('workflows.card.menu.activate')}
      </Button>
    ),
    <Button
      key="delete"
      type="button"
      intent="danger"
      isSecondary
      icon={<FaTrash />}
      onClick={onDelete}
    >
      {t('workflows.card.menu.delete')}
    </Button>
  ]

  return (
    <StyledCard onClick={onOpen}>
      <ThumbnailWrapper>
        <WorkflowThumbnail data={workflow.react_flow_data} />
      </ThumbnailWrapper>

      <CardBody>
        <TitleRow>
          <WorkflowName>{workflow.name}</WorkflowName>
          <TitleActions onClick={e => e.stopPropagation()}>
            <Badge type={workflow.enabled ? 'success' : 'default'}>
              {workflow.enabled ? t('workflows.status.active') : t('workflows.status.paused')}
            </Badge>
            <DropdownMenu items={menuItems}>
              <Button
                type="button"
                isSecondary
                size="sm"
                icon={<FaEllipsisH />}
                aria-label={t('workflows.card.menu.aria')}
              />
            </DropdownMenu>
          </TitleActions>
        </TitleRow>

        <MetaRow>
          <span>{t('workflows.card.nodeCount', { count: nodeCount })}</span>
          <MetaDivider />
          <span>{getDateLabel(workflow, sortField, t)}</span>
        </MetaRow>
      </CardBody>
    </StyledCard>
  )
}

const StyledCard = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.neutralWhite};
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  overflow: hidden;
  cursor: pointer;
  transition: box-shadow 0.15s ease, transform 0.15s ease;

  &:hover {
    box-shadow: ${({ theme }) => theme.shadow.md};
    transform: translateY(-2px);
  }
`

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
`

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const WorkflowName = styled.div`
  flex: 1;
  min-width: 0;
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const TitleActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  flex-shrink: 0;
`

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`

const MetaDivider = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.iconGray};
`
